import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { isHuaweiDevice } from "@/utils/deviceInstallDetection";
const AUTH_BASE_URL = "https://front.microcentrum.cl/v1/auth";
export const MICENTRUM_PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=cl.microcentrum.app&hl=es_CL";
/**
 * Ficha / búsqueda en AppGallery.
 * Si publican una ficha definitiva, reemplazar por el deep link o URL de detalle.
 */
export const MICENTRUM_APP_GALLERY_URL = "https://appgallery.huawei.com/#/search/MiCentrum";
/** Deep link nativo AppGallery por package id (fallback a URL web). */
export const MICENTRUM_APP_GALLERY_MARKET_URL = "appmarket://details?id=cl.microcentrum.app";
/** Ficha oficial de MiCentrum en App Store (Chile). */
export const MICENTRUM_APP_STORE_URL = "https://apps.apple.com/cl/app/micentrum/id6797594067";
const DEFAULT_MANDATORY_MESSAGE = "Hay una nueva versión obligatoria de MiCentrum. Debes actualizar para continuar.";
const DEFAULT_OPTIONAL_MESSAGE = "Hay una nueva versión de MiCentrum disponible.";
const STORE_LABELS = {
    play: "Actualizar en Google Play",
    appgallery: "Actualizar en AppGallery",
    appstore: "Actualizar en App Store",
};
const optionalDismissKey = (channel, latestVersionCode) => `${channel}_optional_update_dismiss_${latestVersionCode}`;
export const fetchAppVersionConfig = async () => {
    try {
        const response = await fetch(`${AUTH_BASE_URL}?op=appVersionConfig`, {
            method: "GET",
        });
        if (!response.ok)
            return null;
        return (await response.json());
    }
    catch (error) {
        console.warn("[AppVersion] No se pudo obtener config de versión:", error);
        return null;
    }
};
/**
 * Canal de política según runtime nativo (clientes nuevos):
 * - iOS → ios
 * - Android Huawei/Honor → huawei
 * - Android (Google Play) → play
 *
 * Nota: la clave JSON `android` es LEGACY (apps viejas); no se usa aquí para Play.
 */
export const resolveUpdateChannel = () => {
    if (!Capacitor.isNativePlatform())
        return null;
    const platform = Capacitor.getPlatform();
    if (platform === "ios")
        return "ios";
    if (platform === "android") {
        return isHuaweiDevice() ? "huawei" : "play";
    }
    return null;
};
export const toStoreChannel = (updateChannel) => {
    if (updateChannel === "huawei")
        return "appgallery";
    if (updateChannel === "ios")
        return "appstore";
    return "play";
};
export const getInstalledNativeVersionCode = async () => {
    try {
        const info = await App.getInfo();
        const code = Number.parseInt(String(info.build ?? ""), 10);
        return Number.isFinite(code) ? code : null;
    }
    catch (error) {
        console.warn("[AppVersion] No se pudo leer build instalado:", error);
        return null;
    }
};
/** @deprecated Usar getInstalledNativeVersionCode */
export const getInstalledAndroidVersionCode = getInstalledNativeVersionCode;
export const evaluateNativeUpdate = (currentVersionCode, policy) => {
    const { minVersionCode, latestVersionCode, forceUpdate, message } = policy;
    if (currentVersionCode < minVersionCode) {
        return {
            kind: "mandatory",
            message: message?.trim() || DEFAULT_MANDATORY_MESSAGE,
        };
    }
    if (forceUpdate && currentVersionCode < latestVersionCode) {
        return {
            kind: "mandatory",
            message: message?.trim() || DEFAULT_MANDATORY_MESSAGE,
        };
    }
    if (currentVersionCode < latestVersionCode) {
        return {
            kind: "optional",
            message: message?.trim() || DEFAULT_OPTIONAL_MESSAGE,
            latestVersionCode,
        };
    }
    return { kind: "none" };
};
/** @deprecated Usar evaluateNativeUpdate */
export const evaluateAndroidUpdate = evaluateNativeUpdate;
export const wasOptionalUpdateDismissed = (latestVersionCode, channel = "play") => sessionStorage.getItem(optionalDismissKey(channel, latestVersionCode)) ===
    "1";
export const dismissOptionalUpdate = (latestVersionCode, channel = "play") => {
    sessionStorage.setItem(optionalDismissKey(channel, latestVersionCode), "1");
};
export const openMicentrumPlayStore = async () => {
    if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: MICENTRUM_PLAY_STORE_URL });
        return;
    }
    window.location.assign(MICENTRUM_PLAY_STORE_URL);
};
export const openMicentrumAppGallery = async () => {
    const webUrl = MICENTRUM_APP_GALLERY_URL.trim() ||
        "https://appgallery.huawei.com/#/search/MiCentrum";
    if (Capacitor.isNativePlatform()) {
        try {
            await Browser.open({ url: MICENTRUM_APP_GALLERY_MARKET_URL });
            return;
        }
        catch (error) {
            console.warn("[AppVersion] No se pudo abrir deep link AppGallery, usando web:", error);
            await Browser.open({ url: webUrl });
            return;
        }
    }
    window.location.assign(webUrl);
};
export const openMicentrumAppStore = async () => {
    if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: MICENTRUM_APP_STORE_URL });
        return;
    }
    window.location.assign(MICENTRUM_APP_STORE_URL);
};
/** Abre la tienda correcta según el dispositivo nativo. */
export const openMicentrumStoreForDevice = async () => {
    const channel = resolveUpdateChannel();
    if (channel === "huawei") {
        await openMicentrumAppGallery();
        return;
    }
    if (channel === "ios") {
        await openMicentrumAppStore();
        return;
    }
    await openMicentrumPlayStore();
};
export const getUpdateStoreLabel = (channel = resolveUpdateChannel()) => {
    if (!channel)
        return STORE_LABELS.play;
    return STORE_LABELS[toStoreChannel(channel)];
};
const pickPolicy = (config, channel) => {
    if (!config)
        return null;
    if (channel === "huawei") {
        // Huawei nuevo → huawei; si falta, legacy `android` (mismo default histórico).
        return config.huawei ?? config.android ?? null;
    }
    if (channel === "ios") {
        return config.ios ?? null;
    }
    // Google Play: solo `play` (nunca la clave legacy `android`).
    return config.play ?? null;
};
export const checkNativeAppUpdate = async () => {
    const empty = {
        visible: false,
        mandatory: false,
        message: "",
    };
    const channel = resolveUpdateChannel();
    if (!channel)
        return empty;
    const [config, currentVersionCode] = await Promise.all([
        fetchAppVersionConfig(),
        getInstalledNativeVersionCode(),
    ]);
    const policy = pickPolicy(config, channel);
    if (!policy || currentVersionCode == null) {
        return empty;
    }
    const result = evaluateNativeUpdate(currentVersionCode, policy);
    const storeChannel = toStoreChannel(channel);
    const storeLabel = STORE_LABELS[storeChannel];
    const useAppGallery = storeChannel === "appgallery";
    if (result.kind === "mandatory") {
        return {
            visible: true,
            mandatory: true,
            message: result.message,
            storeLabel,
            storeChannel,
            useAppGallery,
        };
    }
    if (result.kind === "optional") {
        if (wasOptionalUpdateDismissed(result.latestVersionCode, channel)) {
            return empty;
        }
        return {
            visible: true,
            mandatory: false,
            message: result.message,
            latestVersionCode: result.latestVersionCode,
            storeLabel,
            storeChannel,
            useAppGallery,
        };
    }
    return empty;
};
/** Cada 15 min en memoria; al navegar o volver a primer plano (debounce corto). */
const NATIVE_UPDATE_INTERVAL_MS = 15 * 60 * 1000;
const NATIVE_UPDATE_RESUME_GAP_MS = 45 * 1000;
let nativeUpdateEmit = null;
/** Fuerza o reutiliza el chequeo del watcher (botón Actualizar o cambio de ruta). */
export const requestNativeUpdateCheck = async (force = false) => {
    if (!nativeUpdateEmit)
        return;
    await nativeUpdateEmit(force);
};
/**
 * Vigila versiones en cualquier instalación nativa (Play, AppGallery, iOS).
 * No corre en web/PWA. Devuelve cleanup.
 */
export const startNativeAppUpdateWatcher = (onUpdate) => {
    if (!Capacitor.isNativePlatform())
        return () => { };
    let lastCheckAt = 0;
    let disposed = false;
    let intervalId = null;
    let appStateHandle = null;
    const emit = async (force) => {
        if (disposed)
            return;
        const now = Date.now();
        if (!force && now - lastCheckAt < NATIVE_UPDATE_RESUME_GAP_MS)
            return;
        lastCheckAt = now;
        try {
            const updateState = await checkNativeAppUpdate();
            onUpdate({
                ...updateState,
                updateChannel: resolveUpdateChannel() || undefined,
            });
        }
        catch (error) {
            console.warn("[AppVersion] Falló chequeo periódico:", error);
        }
    };
    nativeUpdateEmit = emit;
    void emit(true);
    intervalId = setInterval(() => {
        void emit(true);
    }, NATIVE_UPDATE_INTERVAL_MS);
    const onVisibility = () => {
        if (document.visibilityState === "visible")
            void emit(false);
    };
    document.addEventListener("visibilitychange", onVisibility);
    void App.addListener("appStateChange", ({ isActive }) => {
        if (isActive)
            void emit(false);
    }).then((handle) => {
        if (disposed) {
            void handle.remove();
            return;
        }
        appStateHandle = handle;
    });
    return () => {
        disposed = true;
        if (nativeUpdateEmit === emit)
            nativeUpdateEmit = null;
        if (intervalId)
            clearInterval(intervalId);
        document.removeEventListener("visibilitychange", onVisibility);
        void appStateHandle?.remove();
    };
};
/** @deprecated Usar checkNativeAppUpdate */
export const checkAndroidAppUpdate = checkNativeAppUpdate;
